import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import '../../pcos-management-style.css';
import { apiGet, apiPost } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useNotification } from '../../context/CustomDialogContext';
import { useLeadModal } from '../../context/LeadModalContext';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import CourseEnrollModal from '../../components/CourseEnrollModal';
import courseBgImage from '../../assets/images/courses/course_bg.webp';
import heroCourseImage from '../../assets/images/courses/300hr-yttc.webp';

/* ─── Accordion Item (Syllabus & FAQ) ─────────────────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {num && <span className="fy-accordion-num">{num}</span>}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon"><i className="bi bi-plus"></i></div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const YogaTTC300 = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCountry, formatPrice } = useCountry();
  const { showAlert } = useNotification();
  const { openModal } = useLeadModal();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const courseTitle = "300 Hour Yoga Teacher Training";

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    address: '',
    countryCode: '+91',
    phone: '',
    email: '',
    batch_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [dbCourse, setDbCourse] = useState(null);
  const [demoInfo, setDemoInfo] = useState(null);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  /* Fetch active coupons */
  useEffect(() => {
    async function fetchCoupons() {
      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      }
    }
    fetchCoupons();
  }, []);

  /* 1-second interval for real-time coupon countdown */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponFor300Hour = () => {
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    if (match.status && match.status !== 'active') return null;

    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) return null;
    }
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Expired';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    parts.push(`${hours} hr`);
    parts.push(`${minutes} min`);
    parts.push(`${seconds} sec`);

    return parts.join(' ');
  };

  /* Scoped CSS class for design system */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Load 300 Hour course data & batches & demo availability */
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        let currentCourseId = 0;
        let targetTitle = courseTitle;
        if (coursesData && Array.isArray(coursesData)) {
          let match = coursesData.find(
            c => c.title && c.title.toLowerCase().trim() === courseTitle.toLowerCase().trim()
          );
          if (!match) {
            match = coursesData.find(
              c => (c.slug && c.slug.toLowerCase().trim() === '300hr-yoga-ttc') ||
                   (c.course_slug && c.course_slug.toLowerCase().trim() === '300hr-yoga-ttc') ||
                   (c.title && c.title.toLowerCase().includes('300 hour')) ||
                   (c.title && c.title.toLowerCase().includes('300hr'))
            );
          }
          if (match) {
            setDbCourse(match);
            currentCourseId = match.id;
            if (match.title) {
              targetTitle = match.title;
            }
          }
        }

        // Query upcoming batches for 300 Hour YTTC
        let batchesData = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(targetTitle)}`);
        
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && targetTitle !== courseTitle) {
          const fallbackBatches = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(courseTitle)}`);
          if (fallbackBatches && Array.isArray(fallbackBatches) && fallbackBatches.length > 0) {
            batchesData = fallbackBatches;
          }
        }

        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && currentCourseId > 0) {
          const idBatches = await apiGet(`/get-upcoming-batches.php?course_id=${currentCourseId}`);
          if (idBatches && Array.isArray(idBatches) && idBatches.length > 0) {
            batchesData = idBatches;
          }
        }

        setUpcomingBatches(batchesData || []);

        try {
          if (currentCourseId > 0) {
            const demoRes = await apiGet(`/public/check-demo-course.php?course_id=${currentCourseId}&slug=300hr-yoga-ttc`);
            if (demoRes && demoRes.available) {
              setDemoInfo(demoRes);
            }
          }
        } catch (err) {
          console.log("No demo configuration for this course.");
        }
      } catch (err) {
        console.error('Failed to load course details & batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    }
    loadData();
  }, []);

  /* Sync logged-in student data */
  useEffect(() => {
    if (user && user.role === 'student') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        phone: user.mobile_number || user.phone || '',
        father_name: user.father_name || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  /* SEO & smooth scroll */
  useEffect(() => {
    document.title = "300 Hour Yoga Teacher Training Rishikesh | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        "Advance your path as a certified yoga teacher with PranaFlow Academy's Online 300 Hour Yoga Teacher Training from Rishikesh. Live daily sessions, expert partner teachers, batches of 25. Enroll now.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses/300hr-yoga-ttc");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "300 Hour Yoga Teacher Training Rishikesh | PranaFlow Academy" },
      { property: 'og:description', content: "Advance your path as a certified yoga teacher with PranaFlow Academy's Online 300 Hour Yoga Teacher Training from Rishikesh. Live daily sessions, expert partner teachers, batches of 25. Enroll now." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses/300hr-yoga-ttc" },
      { property: 'og:image', content: "https://pranaflowacademy.com/images/courses/300hr-yttc.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter / X Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "300 Hour Yoga Teacher Training Rishikesh | PranaFlow Academy" },
      { name: 'twitter:description', content: "Advance your path as a certified yoga teacher with PranaFlow Academy's Online 300 Hour Yoga Teacher Training from Rishikesh. Live daily sessions, expert partner teachers, batches of 25. Enroll now." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/images/courses/300hr-yttc.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  /* Schema injection */
  useEffect(() => {
    const scriptId = 'jsonld-schema-300h';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/courses/300hr-yoga-ttc#course",
      "name": "300 Hour Yoga Teacher Training Course",
      "description": "PranaFlow Academy's 300 Hour Yoga Teacher Training Course is designed for yoga practitioners and teachers who want to deepen their knowledge, teaching skills, yoga philosophy, practice, and professional confidence.",
      "url": "https://pranaflowacademy.com/courses/300hr-yoga-ttc",
      "provider": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Certificate of Completion",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "300 Hour Yoga Teacher Training Course",
          "item": "https://pranaflowacademy.com/courses/300hr-yoga-ttc"
        }
      ]
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const displayPrice = dbCourse && parseFloat(dbCourse.fee_inr) > 0 ? formatPrice(dbCourse.fee_inr, dbCourse) : (dbCourse ? 'Fee will be updated soon' : 'Loading...');

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showAlert({ title: "Warning", message: "Student Name is required.", type: "warning" }); return; }
    if (!formData.email.trim()) { showAlert({ title: "Warning", message: "Email address is required.", type: "warning" }); return; }
    if (!formData.countryCode.trim()) { showAlert({ title: "Warning", message: "Country Code is required.", type: "warning" }); return; }
    if (!formData.phone.trim()) { showAlert({ title: "Warning", message: "Please enter your mobile number.", type: "warning" }); return; }

    const isInactive = dbCourse && dbCourse.status === 'inactive';

    if (isInactive) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData.name.trim(),
          father_name: formData.father_name ? formData.father_name.trim() : null,
          mobile: formData.phone.trim(),
          country_code: formData.countryCode.trim(),
          mobile_number: formData.phone.trim(),
          country_iso_code: selectedCountry ? selectedCountry.country_code : 'IN',
          email: formData.email.trim(),
          address: formData.address ? formData.address.trim() : null,
          course_id: dbCourse ? dbCourse.id : null,
          country: selectedCountry ? selectedCountry.country_name : null,
          message: "I am interested in this course. Please contact me when new batches are available.",
          source_page: window.location.pathname
        };

        await apiPost('/save-interested-lead.php', payload);
        setIsSuccess(true);
      } catch (err) {
        showAlert({ title: "Error", message: err.message || "Failed to submit details. Please try again.", type: "error" });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!formData.father_name.trim()) { showAlert({ title: "Warning", message: "Father's / Guardian's Name is required.", type: "warning" }); return; }
    if (!formData.address.trim()) { showAlert({ title: "Warning", message: "Full Address is required.", type: "warning" }); return; }
    if (!formData.batch_id) { showAlert({ title: "Warning", message: "Please select an upcoming batch.", type: "warning" }); return; }

    const selectedBatch = upcomingBatches.find(b => Number(b.id) === Number(formData.batch_id));
    if (!selectedBatch) { showAlert({ title: "Warning", message: "Selected batch not found.", type: "warning" }); return; }

    navigate('/enroll-confirm', {
      state: {
        studentDetails: {
          name: formData.name,
          father_name: formData.father_name,
          address: formData.address,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email
        },
        courseDetails: {
          id: dbCourse ? dbCourse.id : null,
          title: dbCourse ? dbCourse.title : courseTitle,
          duration: dbCourse ? dbCourse.duration : '300 Days',
          fee_inr: dbCourse ? dbCourse.fee_inr : 0,
          fee_usd: dbCourse ? dbCourse.fee_usd : 0
        },
        batchDetails: {
          id: selectedBatch.id,
          name: selectedBatch.batch_name,
          timing: selectedBatch.batch_timing,
          start_date: selectedBatch.start_date
        }
      }
    });
  };

  /* 11 Core Subjects Syllabus Data */
  const modules = [
    {
      num: '01',
      title: 'Hatha Yoga',
      desc: 'Hatha Yoga forms the classical foundation of your advanced training. You will refine traditional postures, breath coordination, body stability, and the meditative quality of sustained practice.'
    },
    {
      num: '02',
      title: 'Ashtanga Yoga',
      desc: 'Explore the structure, discipline, and methodology of Ashtanga practice, with focus on sequence, breath, rhythm, and advanced teaching application.'
    },
    {
      num: '03',
      title: 'Vinyasa Flow',
      desc: 'Develop intelligent sequencing, transition awareness, and dynamic teaching skills for modern flow-based classes.'
    },
    {
      num: '04',
      title: 'Yoga Philosophy',
      desc: 'Study Patanjali’s Yoga Sutras, the Bhagavad Gita, the Upanishads, and classical yogic concepts in a way that strengthens both your practice and your teaching.'
    },
    {
      num: '05',
      title: 'Yoga Anatomy and Physiology',
      desc: 'Build a deeper understanding of muscles, joints, fascia, the nervous system, and movement principles relevant to advanced yoga practice.'
    },
    {
      num: '06',
      title: 'Pranayama (Breathing Practices)',
      desc: 'Learn advanced breathing techniques, breath control, and safe teaching methods for pranayama-based sessions.'
    },
    {
      num: '07',
      title: 'Teaching Methodology',
      desc: 'Refine your class design, cueing, sequencing, observation, student management, and teacher presence.'
    },
    {
      num: '08',
      title: 'Asana Alignment and Adjustment',
      desc: 'Develop a more precise understanding of safe alignment, hands-on guidance, and intelligent correction language.'
    },
    {
      num: '09',
      title: 'Mantra Chanting',
      desc: 'Learn how mantra supports focus, tradition, and the deeper atmosphere of advanced yoga practice.'
    },
    {
      num: '10',
      title: 'Meditation',
      desc: 'Explore meditation practices that deepen concentration, awareness, and inner discipline.'
    },
    {
      num: '11',
      title: 'Evaluation & Practicum',
      desc: 'Your learning is assessed through practice teaching, assignments, attendance, and final evaluation.'
    }
  ];

  /* 6 Core Pillars */
  const pillars = [
    {
      num: '01',
      title: 'Refine Your Personal Practice',
      tags: ['Advanced Asana', 'Alignment', 'Postural Precision'],
      desc: 'Refine classical Hatha postures, alignment stability, body awareness, and physical posture mastery through daily live practice.'
    },
    {
      num: '02',
      title: 'Understand Different Yoga Styles',
      tags: ['Hatha Yoga', 'Ashtanga Yoga', 'Vinyasa Flow'],
      desc: 'Gain mastery across traditional Hatha, dynamic Ashtanga primary series principles, and creative Vinyasa Flow sequencing.'
    },
    {
      num: '03',
      title: 'Understand the Body Behind the Pose',
      tags: ['Functional Anatomy', 'Physiology', 'Joint Biomechanics'],
      desc: 'Study muscles, fascia, joints, nervous system regulation, and safe injury-prevention principles for advanced bodies.'
    },
    {
      num: '04',
      title: 'Refine Alignment & Adaptation',
      tags: ['Posture Adjustments', 'Props Modifications', 'Safety Cues'],
      desc: 'Learn articulate verbal correction language, posture modifications, props integration, and safe alignment guidance.'
    },
    {
      num: '05',
      title: 'Deepen Philosophy, Pranayama & Meditation',
      tags: ['Yoga Sutras', 'Advanced Breathwork', 'Dharana & Dhyana'],
      desc: 'Explore Patanjali’s Yoga Sutras, Bhagavad Gita wisdom, advanced pranayama techniques, and quiet meditation tools.'
    },
    {
      num: '06',
      title: 'Develop Your Professional Teaching Method',
      tags: ['Class Architecture', 'Teaching Practicum', 'Evaluation'],
      desc: 'Design advanced class sequences, lead live practice teaching sessions, receive mentor feedback, and earn your certificate.'
    }
  ];

  /* Common Teaching Challenges */
  const challengePoints = [
    { title: "Surface-level class sequencing", desc: "Randomly putting postures together without clear anatomical or energetic class flow logic" },
    { title: "Superficial anatomical cues", desc: "Teaching alignment cues without understanding joint limits, muscle origins, or individual structural differences" },
    { title: "Struggling to teach multiple styles", desc: "Lacking confidence to switch between Hatha, Ashtanga, and Vinyasa Flow class structures" },
    { title: "Shallow understanding of breathwork", desc: "Teaching basic breathing without mastering advanced pranayama, ratios, or nervous system effects" },
    { title: "Uncertainty with classical philosophy", desc: "Studying Sutras or Gita without knowing how to weave philosophical themes into posture classes" },
    { title: "Weak verbal presence & cueing", desc: "Struggling to command space, project a steady voice, or give clear non-jarring alignment cues" },
    { title: "Difficulty adapting for tight bodies", desc: "Not knowing how to intelligently modify advanced postures using props, variations, and safety walls" },
    { title: "Relying on pre-recorded videos", desc: "Learning passively from videos without real-time instructor observation, correction, and live Q&A" },
    { title: "Lack of structured teaching practicum", desc: "Completing theory without leading live practice teaching sessions and receiving constructive feedback" },
    { title: "Feeling stuck as a 200-hour teacher", desc: "Needing advanced 300-hour training to step into senior teaching roles, workshops, and retreats" },
    { title: "Isolation in self-guided study", desc: "Lacking a supportive batch environment and dedicated lead teacher mentorship throughout your training" },
    { title: "Needing recognized 300-hour credentials", desc: "Seeking a complete 300-hour advanced certificate from Rishikesh, India to build professional authority" }
  ];

  const highlights = [
    "300 Hours of Live Interactive Advanced Instruction",
    "Completed Across 300 Training Days (~1 Hour Daily)",
    "Master 11 Comprehensive Core Subjects",
    "Classical Hatha, Ashtanga Primary Series & Vinyasa Flow",
    "Functional Anatomy, Physiology & Joint Biomechanics",
    "Patanjali's Yoga Sutras & Bhagavad Gita Philosophy",
    "Advanced Asana Alignment, Adjustments & Modifications",
    "Advanced Pranayama & Breath Control Techniques",
    "Meditation, Concentration (Dharana) & Dhyana Training",
    "Mantra Chanting & Sanskrit Sound Resonance",
    "Class Architecture, Peak Pose Sequencing & Pacing",
    "PranaFlow Student Dashboard Portal Access",
    "Same-Day Class Video Recordings for Catch-Up",
    "Small Interactive Batches (Max 25 Students)",
    "PranaFlow Academy 300-Hour Yoga Teacher Certificate"
  ];

  const bonuses = [
    "300-Hour Advanced Manual",
    "11-Subject Study Guides",
    "Asana Alignment Chart",
    "Vinyasa Sequencing Templates",
    "Pranayama & Sutra Guide",
    "Ayurvedic Lifestyle Guide",
    "PranaFlow Certificate"
  ];

  const myths = [
    { m: '"300-hour training is only for full-time professional yoga teachers."', f: 'Many dedicated practitioners join to refine their personal practice, study philosophy deeply, understand anatomy, and experience inner transformation.' },
    { m: '"You must be able to perform extreme advanced poses to enroll."', f: 'Advanced training is about postural clarity, alignment wisdom, anatomy, and teaching refinement — not circus flexibility.' },
    { m: '"Online advanced yoga training is just watching pre-recorded videos."', f: 'PranaFlow Academy delivers live interactive classes with real-time mentor feedback, Q&A, and same-day recording access for revision.' },
    { m: '"A 200-hour certificate is enough for a lifelong teaching career."', f: 'A 200-hour course gives you foundational tools, while a 300-hour advanced training refines your sequencing, anatomy, alignment, and teacher authority.' },
    { m: '"All students in an advanced class can follow the same cues."', f: 'Every body has unique bone structure and flexibility. A 300-hour trained teacher knows how to adapt postures safely for all bodies.' },
    { m: '"You can study advanced philosophy by reading books alone."', f: 'Philosophical integration requires structured study over time, self-inquiry, and guided discussion with a lead teacher.' }
  ];

  const diffPoints = [
    { title: "300 Training Days Journey", desc: "A progressive, steady 300-day curriculum designed for long-term depth and mastery without burnout." },
    { title: "Live Daily Instruction", desc: "Real-time live classes with experienced educators from Rishikesh, India, offering direct guidance." },
    { title: "11 Core Advanced Subjects", desc: "Covers Hatha, Ashtanga, Vinyasa, philosophy, anatomy, pranayama, alignment, mantra, meditation & practicum." },
    { title: "PranaFlow Student Dashboard", desc: "All course recordings, schedule, study guides, and sequencing templates organized in your portal." },
    { title: "Same-Day Recording Access", desc: "Missed a live class due to work or travel? Watch recorded lectures uploaded to your dashboard on the same day." },
    { title: "Practical Teaching Practicum", desc: "Deliver live teaching demonstrations, receive peer and instructor feedback, and refine your teacher voice." },
    { title: "Small Interactive Batches", desc: "Limited to maximum 25 students per batch for direct guidance and personal mentorship." },
    { title: "Advanced Certificate", desc: "Awards the official 300-Hour Yoga Teacher Certificate upon successful completion through one of our verified Yoga Alliance-registered partner schools." }
  ];

  const faqs = [
    { q: "Who can join this 300-hour course?", a: "This course is designed for students who have completed a foundational yoga teacher training or experienced practitioners looking to refine their practice and teaching." },
    { q: "How is the course delivered?", a: "It is delivered live online in daily 1-hour sessions, with recordings uploaded to your private PranaFlow Student Dashboard after class." },
    { q: "What happens if I miss a live class?", a: "All live classes are recorded and uploaded to your dashboard on the same day so you can stay fully aligned with your batch." },
    { q: "Can I rewatch lectures I have already attended?", a: "Yes. All recorded sessions remain available on your dashboard for revision during your access period." },
    { q: "Who issues the certificate?", a: "Your official 300-Hour Yoga Teacher Certificate is issued through one of PranaFlow Academy’s verified Yoga Alliance-registered partner schools." },
    { q: "How many students are in each batch?", a: "Each batch is strictly limited to a maximum of 25 students for personalized attention." },
    { q: "Can I pay tuition in installments?", a: "Yes, flexible installment payment options are available according to configured course plans." },
    { q: "What core subjects are covered?", a: "The course covers 11 core subjects: Hatha, Ashtanga, Vinyasa, philosophy, anatomy, pranayama, methodology, alignment, mantra, meditation & evaluation." },
    { q: "Is this course suitable for international students?", a: "Yes. The live online format and daily recordings accommodate students in India and across international time zones." },
    { q: "Will I receive a Student Dashboard?", a: "Yes, after successful enrollment according to the standard PranaFlow Academy LMS account flow." }
  ];

  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* 1. HERO SECTION */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-star-fill text-warning me-1"></i> Live Online Advanced Course
                  </span>
                  <h1 className="fy-hero-title">300 Hour Yoga Teacher Training Online from Rishikesh</h1>
                  <div className="fy-hero-subhead">Go Beyond Basic Teacher Training. Deepen Your Practice. Refine the Way You Teach.</div>

                  <p className="fy-hero-para">
                    Advanced Live Online Yoga Teacher Training From Rishikesh, India.
                  </p>

                  <p className="fy-hero-para">
                    Advance your path as a certified yoga teacher with PranaFlow Academy's Online 300 Hour Yoga Teacher Training — a structured, expert-led advanced program designed for certified teachers and experienced practitioners who want to deepen their knowledge, refine their teaching methodology, and grow through higher-level training — with all sessions recorded and managed through your PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                    {demoInfo && demoInfo.available && (
                      <button 
                        type="button"
                        className="fy-btn-secondary" 
                        onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || '300hr-yoga-ttc'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      >
                        {demoInfo.demoFee > 0 
                          ? `Demo Class — ₹${Number(demoInfo.demoFee)}` 
                          : 'Demo Class'}
                      </button>
                    )}
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill"></i> 300 Hours Training</span>
                    <span><i className="bi bi-calendar-event"></i> 300 Training Days</span>
                    <span><i className="bi bi-people-fill"></i> Max 25 Per Batch</span>
                    <span><i className="bi bi-award-fill"></i> Academy Certificate</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img src={heroCourseImage} alt="300 Hour Yoga Teacher Training Online" loading="eager" />
                  </div>
                </div>
              </div>

              {/* Feature Callout Strip */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-clock-history"></i></div>
                    <div className="fy-strip-label">300 Hours<br />300 Days</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-camera-video-fill"></i></div>
                    <div className="fy-strip-label">Live Online<br />Training</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-journal-bookmark"></i></div>
                    <div className="fy-strip-label">11 Core<br />Subjects</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-person-check-fill"></i></div>
                    <div className="fy-strip-label">Teaching<br />Practicum</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-speedometer2"></i></div>
                    <div className="fy-strip-label">Student<br />Dashboard</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-credit-card-2-back"></i></div>
                    <div className="fy-strip-label">Installment<br />Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. OVERVIEW — "Where Digital Accessibility Meets Authentic Yoga Education" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">Where Digital Accessibility Meets Authentic Yoga Education</h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    PranaFlow Academy was built on a simple belief: serious yoga education should be accessible to dedicated students anywhere in the world.
                  </p>
                  <p className="fy-hero-para">
                    We connect committed yoga teachers and practitioners with experienced, certified yoga educators from Rishikesh and across India. Through carefully structured live online classes (~1 hour per day over 300 days), we deliver advanced yoga training directly to your screen.
                  </p>

                  <div className="fy-disclaimer-box">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      "We don't just stream yoga classes. We build real yoga teachers." — PranaFlow Academy
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Traditional Hatha, Ashtanga primary series &amp; Vinyasa Flow</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Functional anatomy, joint biomechanics &amp; injury prevention</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Yoga Sutras, Bhagavad Gita &amp; advanced yogic philosophy</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Advanced pranayama techniques, breath control &amp; meditation</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Class architecture, posture alignment &amp; verbal cueing mastery</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard with daily video access</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill"></i><span>Live teaching practicum, mentor evaluation &amp; 300-hour cert</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. HIGHLIGHTS & BONUS — "What You Will Cover" */}
        <section id="highlights" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What You Will Cover</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Hatha Yoga Postural Refinement</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Ashtanga Primary Series &amp; Vinyasa Flow</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga Sutras &amp; Bhagavad Gita Philosophy</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Functional Anatomy &amp; Biomechanics</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Physiology &amp; Nervous System Regulation</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Advanced Pranayama &amp; Breath Education</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Teaching Methodology &amp; Cueing Clarity</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Asana Alignment, Adjustments &amp; Corrections</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Mantra Chanting &amp; Sound Science</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Meditation &amp; Mindful Concentration</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Live Teaching Practicum &amp; Peer Feedback</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Online vs Offline Teaching Mastery</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard Access</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Evaluation &amp; Case Study Assessment</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>300-Hour Advanced Yoga Teacher Certificate</span></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-gift-fill"></i></div>
                  <div className="fy-bonus-title">INCLUDED RESOURCES</div>
                  <div className="fy-bonus-sub">Manuals, Guides &amp; Templates</div>
                  <ul className="fy-bonus-list">
                    {bonuses.map((b, i) => (
                      <li key={i}><i className="bi bi-check2-circle"></i>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRICULUM OVERVIEW — 6 Pillars & 11 Core Subjects Accordion */}
        <section id="curriculum" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">6 Core Pillars of Advanced Teacher Development</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-curriculum-grid mb-4">
              {pillars.map((p, i) => (
                <div key={i} className="fy-week-card">
                  <div className="fy-week-icon"><i className="bi bi-flower2"></i></div>
                  <span className={`fy-week-badge ${i % 2 === 0 ? 'sage' : 'blush'}`}>{p.num}</span>
                  <h3 className="fy-week-title">{p.title}</h3>
                  <ul className="fy-week-list">
                    {p.tags.map((t, j) => <li key={j}>{t}</li>)}
                  </ul>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', marginTop: '10px', lineHeight: 1.5 }}>{p.desc}</p>
                </div>
              ))}
            </div>

            {/* 11 Core Subjects Syllabus Accordion */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Complete 11 Core Subjects Syllabus</h3>
              <p className="fy-hero-para" style={{ maxWidth: '650px', margin: '8px auto 0' }}>
                Click on any subject to view the detailed topics and descriptions in our 300-hour curriculum.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {modules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={m.title}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    {m.desc}
                  </p>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* 5. TEACHING PRACTICUM JOURNEY */}
        <section id="practicum" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Teaching Practicum — Refine Your Teacher Voice</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Practical teaching experience is built into the training so you graduate with authentic confidence.
              </p>
            </div>

            <div className="row gy-4 text-center">
              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>1</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Prepare</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Design advanced class sequences</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>2</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Teach</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Lead live practice teaching sessions</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>3</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Observe</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Analyze student alignment &amp; cues</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>4</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Receive Feedback</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Get direct mentor evaluation</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>5</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Reflect</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Refine voice &amp; cueing clarity</p>
                </div>
              </div>

              <div className="col-md-4 col-lg-2">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '20px 12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#DCF5E6', color: '#1A5C33', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>6</div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 4px' }}>Improve</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--fy-text-muted)', margin: 0 }}>Master professional confidence</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. COURSE DETAILS / WHO IT IS FOR & PRICING CARD */}
        <section id="course-details" className="fy-section">
          <div className="fy-container">
            <div className="row gy-5 align-items-stretch">
              {/* Left image */}
              <div className="col-lg-4" style={{ textAlign: 'center' }}>
                <div className="fy-business-image-wrap">
                  <img src={courseBgImage} alt="300 Hour Yoga Teacher Training Online" className="img-fluid w-100" />
                </div>
              </div>

              {/* Center — Who it is for */}
              <div className="col-lg-4">
                <h2 className="fy-serif-heading mb-3">Who Is This Training For?</h2>
                <div className="text-muted fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Designed for teachers &amp; serious practitioners.</div>

                <p className="fy-hero-para">
                  Certified 200-hour teachers who feel stuck, Hatha/Vinyasa/Ashtanga teachers seeking advanced sequencing, teachers wanting deeper anatomy and pranayama knowledge, and serious practitioners dedicated to long-term growth.
                </p>

                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Graduates of <Link to="/courses/200-hour-yoga-teacher-training-rishikesh">200-hour foundational training</Link></span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Hatha, Vinyasa &amp; Ashtanga yoga teachers</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Teachers seeking functional anatomy mastery</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Teachers wanting confidence with pranayama &amp; philosophy</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Serious yoga practitioners deepening personal practice</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>International students seeking Rishikesh guidance</span></div>
                </div>

                <div className="small text-muted fst-italic">
                  *300 training days delivered live online at approximately 1 hour per day with max 25 students per batch from Rishikesh, India.
                </div>
              </div>

              {/* Right — Pricing card V2 */}
              <div className="col-lg-4">
                {(() => {
                  const coupon = getBannerCouponFor300Hour();
                  const feeInr = dbCourse ? parseFloat(dbCourse.fee_inr) : 0;
                  const displayPrice = dbCourse ? formatPrice(dbCourse.fee_inr, dbCourse) : formatPrice(40000);
                  let formattedOriginalPrice = displayPrice;
                  let formattedDiscountedPrice = '';
                  let percentageVal = 0;

                  if (coupon && feeInr > 0) {
                    if (coupon.discount_type === 'percentage') {
                      percentageVal = Math.round(parseFloat(coupon.discount_value));
                      const discountedInr = feeInr * (1 - percentageVal / 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                      const fixedVal = parseFloat(coupon.discount_value);
                      const discountedInr = Math.max(0, feeInr - fixedVal);
                      percentageVal = Math.round((fixedVal / feeInr) * 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    }
                  }

                  return (
                    <div className="fy-program-details-card-v2" id="pricing">
                      <h3 className="pcos-pd-heading">PROGRAM DETAILS</h3>
                      <div className="pcos-pd-heading-divider"></div>

                      <div className="pcos-pd-rows-wrap">
                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-event"></i>
                            <span>Duration</span>
                          </div>
                          <div className="pcos-pd-val">300 Hours / 300 Days</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-laptop"></i>
                            <span>Mode</span>
                          </div>
                          <div className="pcos-pd-val">Live Online Training</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-clock"></i>
                            <span>Timing</span>
                          </div>
                          <div className="pcos-pd-val">~1 Hour / Day</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-person-badge"></i>
                            <span>Batch Size</span>
                          </div>
                          <div className="pcos-pd-val">Max 25 Students</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-geo-alt"></i>
                            <span>Location</span>
                          </div>
                          <div className="pcos-pd-val">Rishikesh, India</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-journal-check"></i>
                            <span>Curriculum</span>
                          </div>
                          <div className="pcos-pd-val">11 Core Subjects · Advanced</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-speedometer2"></i>
                            <span>Portal</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Dashboard</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-award-fill"></i>
                            <span>Certificate</span>
                          </div>
                          <div className="pcos-pd-val">Yoga Alliance Cert</div>
                        </div>
                      </div>

                      <div className="pf-course-promo-badge pf-course-promo-badge-pd">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        <span>Every batch: First 5 students get an extra discount on fee!</span>
                      </div>

                      {coupon ? (
                        <div className="pcos-pd-offer-box">
                          <div className="pcos-pd-offer-pill">
                            <i className="bi bi-star"></i> LIMITED TIME OFFER
                          </div>

                          <div className="pcos-pd-offer-prices-row">
                            <div className="pcos-pd-orig-price-wrap">
                              <span className="pcos-pd-metric-label">ORIGINAL FEE</span>
                              <span className="pcos-pd-orig-price">{formattedOriginalPrice}</span>
                            </div>

                            <div className="pcos-pd-disc-price-wrap">
                              <span className="pcos-pd-metric-label">DISCOUNTED FEE</span>
                              <span className="pcos-pd-disc-price">{formattedDiscountedPrice}</span>
                            </div>

                            <div className="pcos-pd-badge-wrap">
                              <div className="pcos-pd-badge-rect">
                                <span className="pcos-pd-badge-num">{percentageVal}%</span>
                                <span className="pcos-pd-badge-text">OFF</span>
                              </div>
                            </div>
                          </div>

                          <div className="pcos-pd-timer-strip">
                            <i className="bi bi-stopwatch"></i>
                            <span>
                              {formatCountdown(coupon.banner_expiry || coupon.expiry_date).includes('day') ? 'Offer ends in' : 'Offer valid today'} · {formatCountdown(coupon.banner_expiry || coupon.expiry_date)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="pcos-pd-normal-box">
                          <span className="pcos-pd-normal-label">TUITION FEE</span>
                          <span className="pcos-pd-normal-price">{loadingBatches ? 'Loading...' : displayPrice}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="pcos-pd-enroll-btn"
                        onClick={() => setIsEnrollModalOpen(true)}
                      >
                        <span>Enroll Now &amp; Reserve Your Seat</span>
                        <div className="pcos-pd-btn-arrow-circle">
                          <i className="bi bi-arrow-right"></i>
                        </div>
                      </button>

                      <button
                        type="button"
                        className="fy-btn-secondary w-100 mt-2"
                        onClick={() => openModal(courseTitle, dbCourse)}
                        style={{
                          borderRadius: '30px',
                          padding: '12px 20px',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Enquire Now <i className="bi bi-chat-dots"></i>
                      </button>

                      <div className="pcos-pd-next-batch-strip">
                        <i className="bi bi-leaf"></i>
                        <span>Next batch admissions open — inquire now</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* NEW COMPARISON SECTION: 200 Hour vs 300 Hour vs 500 Hour Yoga Teacher Training */}
        <section id="pathways" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow" style={{ display: 'inline-block', color: 'var(--fy-blush-dark)', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                CHOOSE YOUR TRAINING PATH
              </span>
              <h2 className="fy-serif-heading">200 Hour vs 300 Hour vs 500 Hour Yoga Teacher Training</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '800px', margin: '14px auto 0' }}>
                Yoga teacher training is a progressive learning journey. The right program depends on the foundation you already have, the depth you want to develop, and the direction you want to take your teaching practice.
              </p>
              <p className="fy-hero-para mt-2" style={{ maxWidth: '800px', margin: '8px auto 0', fontSize: '0.95rem', color: 'var(--fy-text-muted)' }}>
                At PranaFlow Academy, the 200-hour pathway focuses on foundational teacher education, the 300-hour pathway develops more advanced knowledge and teaching capability, and the 500-hour pathway provides a longer and more comprehensive approach to yoga study. For students ready to move beyond foundational training, the 300-hour pathway provides the opportunity to deepen both personal practice and professional teaching knowledge.
              </p>
            </div>

            {/* 3 Comparison Cards Grid */}
            <div className="row gy-4 align-items-stretch mb-5">
              {/* Card 1: 200 Hour */}
              <div className="col-lg-4 col-md-6">
                <div style={{
                  background: 'var(--fy-cream-card)',
                  border: '1px solid var(--fy-sage-border)',
                  borderRadius: 'var(--fy-radius-lg)',
                  padding: '32px 24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  boxShadow: 'var(--fy-shadow-card)',
                  transition: 'var(--fy-transition)'
                }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      background: 'var(--fy-sage-light)',
                      color: 'var(--fy-sage-dark)',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '4px 12px',
                      borderRadius: 'var(--fy-radius-pill)',
                      letterSpacing: '0.5px',
                      marginBottom: '14px'
                    }}>
                      FOUNDATIONAL PATHWAY
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-text-main)', marginBottom: '12px', fontFamily: 'var(--heading-font-family)' }}>
                      200 Hour Yoga Teacher Training
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                      The <strong>200 Hour Yoga Teacher Training</strong> establishes the essential foundations of yoga practice and teaching. Students develop structured understanding across asana, pranayama, yoga philosophy, anatomy, alignment, sequencing, teaching methodology, and practical teaching skills. It is best suited to aspiring teachers and dedicated practitioners who are beginning formal yoga teacher education and want a strong foundation before moving into advanced study.
                    </p>
                  </div>
                  <div>
                    <Link
                      to="/courses/200-hour-yoga-teacher-training-rishikesh"
                      className="fy-btn-secondary w-100"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        padding: '12px 18px',
                        borderRadius: 'var(--fy-radius-pill)'
                      }}
                    >
                      Explore the 200 Hour Yoga Teacher Training <i className="bi bi-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Card 2: 300 Hour (THIS COURSE - Highlighted) */}
              <div className="col-lg-4 col-md-12">
                <div style={{
                  background: 'var(--fy-white)',
                  border: '2px solid var(--fy-sage-primary)',
                  borderRadius: 'var(--fy-radius-lg)',
                  padding: '32px 24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  boxShadow: 'var(--fy-shadow-raised)',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '24px',
                    background: 'var(--fy-sage-primary)',
                    color: '#ffffff',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    padding: '3px 12px',
                    borderRadius: 'var(--fy-radius-pill)',
                    letterSpacing: '0.5px'
                  }}>
                    THIS COURSE
                  </div>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      background: 'var(--fy-blush-light)',
                      color: 'var(--fy-blush-dark)',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '4px 12px',
                      borderRadius: 'var(--fy-radius-pill)',
                      letterSpacing: '0.5px',
                      marginBottom: '14px'
                    }}>
                      ADVANCED PATHWAY
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '12px', fontFamily: 'var(--heading-font-family)' }}>
                      300 Hour Yoga Teacher Training
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                      The <strong>300 Hour Yoga Teacher Training</strong> is designed for students who want to progress beyond foundational yoga education and develop greater technical, philosophical, practical, and teaching depth. The program helps students refine teaching methodology, strengthen personal practice, deepen understanding of advanced yoga subjects, and develop greater confidence in applying yoga knowledge within real teaching situations. It is particularly suited to practitioners and yoga teachers who already have foundational training and are ready to study yoga at a more advanced level.
                    </p>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="fy-btn-primary w-100"
                      onClick={() => scrollTo('enrollment')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        padding: '12px 18px',
                        borderRadius: 'var(--fy-radius-pill)'
                      }}
                    >
                      Choose the 300 Hour Advanced Training <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Card 3: 500 Hour */}
              <div className="col-lg-4 col-md-6">
                <div style={{
                  background: 'var(--fy-cream-card)',
                  border: '1px solid var(--fy-sage-border)',
                  borderRadius: 'var(--fy-radius-lg)',
                  padding: '32px 24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  boxShadow: 'var(--fy-shadow-card)',
                  transition: 'var(--fy-transition)'
                }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      background: '#DCF5E6',
                      color: '#1A5C33',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '4px 12px',
                      borderRadius: 'var(--fy-radius-pill)',
                      letterSpacing: '0.5px',
                      marginBottom: '14px'
                    }}>
                      MASTER-LEVEL LEARNING PATHWAY
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-text-main)', marginBottom: '12px', fontFamily: 'var(--heading-font-family)' }}>
                      500 Hour Yoga Teacher Training
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                      The <strong>500 Hour Yoga Teacher Training</strong> is designed for students seeking a substantially longer and more comprehensive yoga education pathway. With 500 hours of live online learning, students have more time to explore yoga practice, philosophy, anatomy, pranayama, teaching methodology, practicum, and other advanced areas while gradually developing greater depth across both personal practice and teaching. It is suited to serious practitioners, existing yoga teachers, and wellness professionals who want sustained learning across a broad and integrated yoga curriculum.
                    </p>
                  </div>
                  <div>
                    <Link
                      to="/courses/500hr-yoga-ttc"
                      className="fy-btn-secondary w-100"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        padding: '12px 18px',
                        borderRadius: 'var(--fy-radius-pill)'
                      }}
                    >
                      Explore the 500 Hour Yoga Teacher Training <i className="bi bi-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose the 300-Hour Pathway? Benefit Grid */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Why Choose the 300 Hour Yoga Teacher Training?</h3>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 mb-5">
              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>01</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Move Beyond Foundational Training</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    Develop a deeper understanding of yoga beyond introductory teacher education and explore more advanced approaches to practice, philosophy, teaching methodology, and professional development.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>02</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Refine Your Teaching Skills</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    Strengthen your ability to sequence classes, communicate concepts, observe students, adapt teaching approaches, and deliver yoga instruction with greater clarity and confidence.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>03</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Deepen Personal Practice and Knowledge</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    Advanced study gives you the opportunity to connect physical practice with philosophy, anatomy, pranayama, teaching methodology, and deeper self-study rather than treating each subject separately.
                  </p>
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px 20px', height: '100%' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>04</div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginBottom: '8px' }}>Grow as a More Knowledgeable Yoga Teacher</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    The 300-hour pathway is designed for students who want continued professional growth and greater depth before taking their teaching journey into more advanced areas.
                  </p>
                </div>
              </div>
            </div>

            {/* Decision / Sales Block */}
            <div style={{
              background: 'linear-gradient(135deg, var(--fy-sage-dark) 0%, var(--fy-sage-primary) 100%)',
              borderRadius: 'var(--fy-radius-lg)',
              padding: '36px 32px',
              color: '#ffffff',
              boxShadow: 'var(--fy-shadow-raised)'
            }}>
              <div className="row align-items-center gy-4">
                <div className="col-lg-8">
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', marginBottom: '16px', fontFamily: 'var(--heading-font-family)' }}>
                    Which Yoga Teacher Training Should You Choose?
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.92rem', color: '#E8F5EB', lineHeight: 1.6 }}>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0', marginTop: '3px' }}></i>
                      <span>Choose the <strong>200-hour pathway</strong> if you need structured foundational yoga teacher training and are beginning your formal teaching journey.</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0', marginTop: '3px' }}></i>
                      <span>Choose the <strong>300-hour pathway</strong> if you already have foundational knowledge and want to deepen your practice, refine your teaching methodology, and develop more advanced teaching skills.</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0', marginTop: '3px' }}></i>
                      <span>Choose the <strong>500-hour pathway</strong> if you want the most extensive learning pathway, with substantially more time to study and integrate yoga across a broad curriculum.</span>
                    </li>
                  </ul>
                </div>

                <div className="col-lg-4 text-center text-lg-end">
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => scrollTo('enrollment')}
                    style={{
                      background: '#ffffff',
                      color: 'var(--fy-sage-dark)',
                      border: 'none',
                      fontWeight: 800,
                      padding: '14px 24px',
                      borderRadius: 'var(--fy-radius-pill)',
                      fontSize: '0.95rem'
                    }}
                  >
                    Choose the 300 Hour Advanced Training <i className="bi bi-arrow-right-circle-fill" style={{ color: 'var(--fy-sage-primary)' }}></i>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* 7. WHAT CHANGES WHEN YOU STUDY YOGA MORE DEEPLY? */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What Changes When You Study Yoga More Deeply?</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '700px', margin: '12px auto 0' }}>
                You learned how to teach yoga. Now learn how to teach it with greater precision, confidence, and purpose.
              </p>
            </div>

            <div className="fy-pain-grid">
              {challengePoints.map((p, i) => (
                <div key={i} className="fy-pain-card">
                  <span className="fy-pain-x">✕</span>
                  <div>
                    <h4 className="fy-pain-title">{p.title}</h4>
                    <span className="fy-pain-desc">{p.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--fy-cream-card)', border: '2px dashed var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 32px', textAlign: 'center', marginTop: '32px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '8px' }}>
                "We don’t just stream yoga classes. We build real yoga teachers." — PranaFlow Academy
              </h3>
              <p style={{ color: 'var(--fy-text-muted)', margin: 0, fontSize: '0.95rem' }}>
                Our 300-hour advanced training provides the depth, live interaction, and structured mentorship required for genuine teaching advancement.
              </p>
            </div>
          </div>
        </section>

        {/* 8. PRANAFLOW STUDENT DASHBOARD */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">PranaFlow LMS Student Portal</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>Missed a Live Class? Your Learning Continues.</h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Work, family, or travel should never disrupt your momentum. Upon enrollment, you receive access to your personal <strong style={{ color: '#ffffff' }}>PranaFlow Student Dashboard</strong>.
                </p>

                <div className="row gy-2 mb-4" style={{ fontSize: '0.95rem', color: '#E8F5EB' }}>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Daily Live Class Links &amp; Schedule</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Same-Day Class Video Recordings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Missed-Class Catch-Up Portal</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Revision Access During Course</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Downloadable Study Manuals &amp; Sutra Guides</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Practicum &amp; Case Study Submission Portal</span></div></div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fbbf24', margin: '0 0 6px' }}>
                    Learn Live. Catch Up When Needed. Master Every Concept.
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    Every live lecture is recorded and uploaded to your Student Dashboard on the same day so you can stay fully aligned with your batch.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Your Student Dashboard Includes:</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>300-Hour Advanced Course Access</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Daily Live Class Schedule &amp; Links</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>11 Core Subject Modules</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Uploaded Lecture Recordings</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Study Manuals &amp; Sequencing Templates</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practicum Case Study Submission</span></li>
                  </ul>
                  <button type="button" className="fy-btn-primary w-100" onClick={() => scrollTo('enrollment')}>
                    Get Access Upon Enrollment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. MYTH-BUSTING */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">300-Hour Advanced Training Myth-Busting</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
                Replacing common misconceptions about advanced 300-hour yoga teacher training.
              </p>
            </div>

            <div className="row gy-3">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">MYTH vs FACT</span>
                    <h4 className="fy-myth-q">{item.m}</h4>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. WHY 300 HOURS? */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Why 300 Hours?</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {diffPoints.map((card, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h4 className="fy-diff-title">{card.title}</h4>
                  <p className="fy-diff-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 11. 300-HOUR ROADMAP */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Your 300-Hour Advanced Learning Journey</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Phase 1</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Styles &amp; Anatomical Precision</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Subjects 01–03 — Hatha, Ashtanga &amp; Vinyasa</li>
                    <li>Subject 05 — Anatomy &amp; Biomechanics</li>
                    <li>Subject 08 — Asana Alignment &amp; Adjustment</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Goal: Master multi-style asana &amp; anatomical cues.
                </div>
              </div>

              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Phase 2</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Philosophy, Breathwork &amp; Mantra</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Subject 04 — Yoga Philosophy &amp; Sutras</li>
                    <li>Subject 06 — Advanced Pranayama</li>
                    <li>Subjects 09–10 — Mantra &amp; Meditation</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Goal: Deepen philosophical depth &amp; breath control.
                </div>
              </div>

              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Phase 3</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Class Design, Practicum &amp; Certification</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Subject 07 — Advanced Teaching Methodology</li>
                    <li>Subject 11 — Evaluation &amp; Teaching Practicum</li>
                    <li>Final Evaluation &amp; 300-Hour Certification</li>
                  </ul>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: '#1A5C33', fontWeight: 600 }}>
                  Goal: Deliver live practicum &amp; earn 300-hour cert.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 12. ENROLLMENT & SEAT RESERVATION */}
        <section id="pricing-form" className="fy-section fy-section-alt">
          <div className="fy-container text-center">
            <div id="enrollment" className="py-2">
              <div className="fy-section-title-wrap text-center mb-3">
                <h2 className="fy-serif-heading" style={{ fontSize: '2rem' }}>
                  Course Enrollment &amp; Seat Reservation
                </h2>
                <div className="fy-title-underline"></div>
              </div>

              <p style={{ fontSize: '1.02rem', color: 'var(--fy-text-muted)', maxWidth: '880px', margin: '0 auto 36px', lineHeight: 1.75 }}>
                Our 300 Hour Advanced Yoga Teacher Training offers comprehensive master-level education with expert instruction from Rishikesh. Due to our strict commitment to personal attention, cohort sizes are strictly limited.
              </p>

              <div className="fy-enrollment-cta-group">
                <button
                  type="button"
                  className="fy-btn-primary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => setIsEnrollModalOpen(true)}
                >
                  Enroll Now &amp; Reserve Your Seat <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </button>

                <button
                  type="button"
                  className="fy-btn-secondary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => openModal(courseTitle, dbCourse)}
                >
                  Enquire Now <i className="bi bi-chat-dots ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 13. WHAT YOU RECEIVE */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow">EVERYTHING INCLUDED</span>
              <h2 className="fy-serif-heading">What You Receive</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Everything included in the 300 Hour Yoga Teacher Training from the moment you enroll.
              </p>
            </div>

            <div className="fy-receive-grid">
              {highlights.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-shield-check"></i>
              <span>All study manuals, live class links, sequencing guides, and dashboard access are unlocked upon registration.</span>
            </div>
          </div>
        </section>

        {/* 14. CERTIFICATE SECTION */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">300 Hour YTTC — Genuine & Verified Certification</h2>
                <p className="fy-hero-para mb-3">
                  Your <strong>300 Hour Yoga Teacher Training Certificate</strong> is issued through one of <strong>PranaFlow Academy’s verified Yoga Alliance-registered partner schools</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  We carefully verify the school’s <strong>Yoga Alliance registration</strong> before establishing a partnership and select partners according to their expertise, teaching standards, experience, and suitability for advanced yoga teacher training.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The applicable partner school for your batch will be communicated during your classes and will also be available in your student dashboard.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  After successfully completing the required training, attendance, practicum, assignments, and final assessment, you will receive a <strong>genuine certificate issued through the applicable verified Yoga Alliance-registered partner school</strong>.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }}></i>
                  <span>Yoga Alliance-Registered Partners · Verified Schools · Genuine Certification</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.8rem', color: 'var(--fy-sage-primary)' }}></i>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    300 Hour Yoga Teacher Certificate
                  </h3>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Issued via Verified Yoga Alliance-Registered Partner School</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-main)', lineHeight: 1.6, margin: 0 }}>
                    Awarded upon completing 300 hours of structured live online training, attendance, practicum, assignments, and final assessment through our verified partner school.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 15. FAQ */}
        <section id="faq" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Frequently Asked Questions</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button className="fy-faq-btn" aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon"><i className="bi bi-plus"></i></div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 16. FINAL CTA BANNER */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">Don't Just Add More Classes to Your Teaching Schedule. Add More Depth to Your Teaching.</h2>
                  <p className="fy-cta-sub">Go Deeper Into Yoga. Refine the Way You Practise. Transform the Way You Teach.</p>
                  <p style={{ fontSize: '0.95rem', color: '#C3DFC8', lineHeight: 1.7, marginBottom: '20px' }}>
                    Master posture alignment, Hatha/Ashtanga/Vinyasa principles, functional anatomy, Sutra philosophy, pranayama mechanics, and live teaching practicum over 300 days with expert guidance managed in your PranaFlow Student Dashboard.
                  </p>
                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary fy-cta-btn-white" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary fy-cta-btn-outline" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                  </div>
                </div>
                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 20px' }}>
                    <div className="fy-cta-logo-wrap">
                      <img 
                        src={getLogoUrl()} 
                        alt="PranaFlow Academy" 
                        className="fy-cta-card-logo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/apple-touch-icon.png'; }}
                      />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', marginBottom: '4px' }}>300 Hours</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>300 Training Days</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>11 Core Subjects</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 17. PROFESSIONAL SCOPE NOTICE */}
        <div className="fy-container mb-5">
          <div className="fy-page-disclaimer">
            <strong>Important Professional &amp; Health Notice:</strong> This course provides comprehensive advanced educational teacher training in traditional yoga, philosophy, pranayama, and teaching methodology. It does not qualify a graduate to practice clinical medicine, diagnose diseases, or provide medical treatment. Participants should practice mindfully within their personal physical limits.
          </div>
        </div>

      </main>

      <CourseEnrollModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={courseTitle}
        formData={formData}
        setFormData={setFormData}
        upcomingBatches={upcomingBatches}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        handleEnrollSubmit={handleEnrollSubmit}
      />

      <PublicFooter />
    </div>
  );
};

export default YogaTTC300;
